import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import { ConsoleAdapter, getRequestContext, initLogger, MultiAdapter } from "./index";
import type { LogContext } from "./logger.context";
import { SentryLogger } from "./sentry.logger.processor";

export function shouldEnableSentry(config: WebAppConfig): boolean {
  if (config.currentLogger !== "SENTRY" || !config.sentryDsn.trim()) {
    return false;
  }
  const env = String(config.sentryEnvironment);
  return env === "DEV" || env === "STAGE" || env === "PROD" || env === "BETA";
}

export class LoggerFactory {
  private static instance: LoggerFactory;

  public static getInstance(): LoggerFactory {
    if (!LoggerFactory.instance) {
      LoggerFactory.instance = new LoggerFactory();
    }
    return LoggerFactory.instance;
  }

  private constructor() {
    const config = WebAppConfig.getInstance();
    const sessionUser = sessionRepository.getUserInfo();

    const logContext: LogContext = {
      environment: config.sentryEnvironment,
      version: config.version,
      user: {
        username: [sessionUser?.firstName, sessionUser?.lastName].filter(Boolean).join(" ") || undefined,
        email: sessionUser?.email,
      },
    };

    const adapters = [];

    if (!config.isProduction()) {
      adapters.push(new ConsoleAdapter());
    }

    if (shouldEnableSentry(config)) {
      adapters.push(
        new SentryLogger({
          dsn: config.sentryDsn,
          context: logContext,
          environment: config.sentryEnvironment,
          replaysSessionSampleRate: config.isProduction() ? 0.1 : 1,
          replaysOnErrorSampleRate: config.isProduction() ? 0.1 : 1,
        }),
      );
    }

    initLogger(
      {
        service_name: config.sentryServiceName,
        environment: config.sentryEnvironment,
        min_level: config.logLevel,
        hide_meta: true,
        global_meta: {
          version: config.version,
          user: logContext.user,
        },
        context_provider: getRequestContext,
      },
      new MultiAdapter(adapters),
    );
  }
}
